import React from "react";
import { useFinancials } from "../../hooks/useFinancials";
import FinancialsFilterBar from "./FinancialsFilterBar";
import StatCard from "./StatCard";
import LoadingSpinner from "../../../../Component/Shared/LoadingSpinner/LoadingSpinner";
import {
  RevenueIcon, InProgressIcon, GrossProfitIcon, ExpensesIcon,
  NetProfitIcon, ProductCostIcon, DeliveredIcon, MarginIcon,
} from "../../../../Component/Shared/Icon/Icon"; 
import styles from "./FinancialsTab.module.css";

const fmt = (n) => (n ?? 0).toLocaleString("ar-EG", { maximumFractionDigits: 2 });

const FinancialsTab = () => {
  const { data, loading, error, filters, setFilters, loadIncomeStatement, resetFilters } = useFinancials();

  const handleApply = () => loadIncomeStatement(filters);

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <h2>نظرة عامة على الأداء المالي</h2>
      </div>

      <FinancialsFilterBar
        filters={filters}
        setFilters={setFilters}
        onApply={handleApply}
        onReset={resetFilters}
      />

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <LoadingSpinner text="جاري تحميل البيانات المالية..." />
      ) : data ? (
        <>
          <div className={styles.periodBar}>
            الفترة من <strong>{new Date(data.from).toLocaleDateString("ar-EG")}</strong> إلى{" "}
            <strong>{new Date(data.to).toLocaleDateString("ar-EG")}</strong>
          </div>

          {/* الصف الأول: الإيرادات */}
          <div className={styles.sectionTitle}>الإيرادات</div>
          <div className={styles.cardsGrid}>
            <StatCard
              icon={<DeliveredIcon />}
              label="الإيراد الفعلي (المُسلَّم)"
              value={`${fmt(data.actualDeliveredRevenue)} ج.م`}
              sub={`${fmt(data.actualDeliveredCount)} طلب مُسلَّم`}
              variant="success"
            />
            <StatCard
              icon={<InProgressIcon />}
              label="قيمة الطلبات الجارية"
              value={`${fmt(data.inProgressOrdersTotalValue)} ج.م`}
              sub={`${fmt(data.inProgressOrdersCount)} طلب جاري`}
              variant="warning"
            />
            <StatCard
              icon={<RevenueIcon />}
              label="الإيراد المتوقع من الجاري"
              value={`${fmt(data.expectedInProgressRevenue)} ج.م`}
              sub={`نسبة التسليم المتوقعة ${fmt(data.expectedDeliveryPercentage)}%`}
              variant="info"
            />
            <StatCard
              icon={<RevenueIcon />}
              label="إجمالي الإيرادات"
              value={`${fmt(data.totalRevenue)} ج.م`}
              variant="primary"
              highlight
            />
          </div>

          {/* الصف الثاني: التكاليف والربح الإجمالي */}
          <div className={styles.sectionTitle}>التكاليف والربح الإجمالي</div>
          <div className={styles.cardsGrid}>
            <StatCard
              icon={<ProductCostIcon />}
              label="إجمالي تكلفة المنتجات"
              value={`${fmt(data.totalProductCost)} ج.م`}
              sub={`نسبة التكلفة ${fmt(data.productCostPercentage)}%`}
              variant="danger"
            />
            <StatCard
              icon={<GrossProfitIcon />}
              label="مجمل الربح"
              value={`${fmt(data.grossProfit)} ج.م`}
              variant="success"
              highlight
            />
          </div>

          {/* الصف الثالث: المصروفات التشغيلية */}
          <div className={styles.sectionTitle}>المصروفات التشغيلية</div>
          <div className={styles.cardsGrid}>
            <StatCard icon={<ExpensesIcon />} label="مصروفات التسويق" value={`${fmt(data.marketingExpenses)} ج.م`} variant="muted" />
            <StatCard icon={<ExpensesIcon />} label="عمولة الوكالة/المشتري الإعلاني" value={`${fmt(data.agencyMediaBuyerExpenses)} ج.م`} variant="muted" />
            <StatCard icon={<ExpensesIcon />} label="المرتبات والعمولات" value={`${fmt(data.salariesAndCommissionsExpenses)} ج.م`} variant="muted" />
            <StatCard icon={<ExpensesIcon />} label="مصروفات ثابتة" value={`${fmt(data.fixedExpenses)} ج.م`} variant="muted" />
            <StatCard icon={<ExpensesIcon />} label="مصروفات أخرى" value={`${fmt(data.otherExpenses)} ج.م`} variant="muted" />
            <StatCard
              icon={<ExpensesIcon />}
              label="إجمالي المصروفات التشغيلية"
              value={`${fmt(data.totalOperatingExpenses)} ج.م`}
              variant="danger"
              highlight
            />
          </div>

          {/* الصف الرابع: صافي الربح */}
          <div className={styles.sectionTitle}>النتيجة النهائية</div>
          <div className={styles.cardsGrid}>
            <StatCard
              icon={<NetProfitIcon />}
              label="صافي الربح"
              value={`${fmt(data.netProfit)} ج.م`}
              variant="success"
              highlight
              big
            />
            <StatCard
              icon={<MarginIcon />}
              label="هامش صافي الربح"
              value={`${fmt(data.netProfitMarginPercentage)}%`}
              variant="primary"
              highlight
              big
            />
          </div>
        </>
      ) : (
        <div className={styles.empty}>لا توجد بيانات للفترة المحددة</div>
      )}
    </div>
  );
};

export default FinancialsTab;